/**
 * KNK-1.7 / FR-45 — rep profile basics and Offices: the rules and copy behind
 * Settings → Knock Mode → Offices and Field profiles.
 *
 * ⚠️ Nothing here is the gate. `save_field_rep_profile`, `set_team_office` and
 * `set_team_office_parent` (migration 20261009a1) refuse everything this screen hides, and the
 * database, not this file, enforces "reps never join the Office" and "at most one Office".
 *
 * Reach follows management (KNK-1.3): an Office's manager reaches every team under it; being a
 * member of anything in the Office reaches nobody. That sentence is the copy below, and the
 * test asserts on it because it is the only place the screen explains the rule.
 */

import { parseDeactivation, type FieldDeactivation } from '@/lib/fieldDeactivation';

export type FieldRole = 'setter' | 'closer' | 'both';
export type WorkerType = 'w2' | '1099';

export interface FieldOffice {
  id: number;
  name: string;
  timezone: string | null;
}

export interface FieldProfile {
  userId: string;
  name: string;
  email: string | null;
  displayName: string | null;
  fieldStartDate: string | null;
  fieldStartDateSet: boolean;
  fieldRole: FieldRole | null;
  fieldRoleSet: boolean;
  active: boolean;
  activeFrom: string | null;
  activeUntil: string | null;
  workerType: WorkerType | null;
  quietStart: string;
  quietEnd: string;
  workingDays: number[] | null;
  hasHomeAddress: boolean;
  office: FieldOffice | null;
  teams: { id: number; name: string; officeId: number | null }[];
  teamHistory: { teamId: number | null; teamName: string; joinedAt: string; leftAt: string | null }[];
  /**
   * KNK-1.54 / FR-46. Null unless the rep has an OPEN deactivation in this account.
   *
   * ⚠️ `active: false` and `deactivation !== null` are different things and the screen must not
   * conflate them. A seasonal rep whose `activeUntil` has passed is inactive with no
   * deactivation; a deactivated rep is both. Only the second has work that moved and a Knock
   * quarantine to settle. Rules in `lib/fieldDeactivation.ts`.
   */
  deactivation: FieldDeactivation | null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const ROLES: readonly FieldRole[] = ['setter', 'closer', 'both'];
const WORKERS: readonly WorkerType[] = ['w2', '1099'];

export function parseFieldProfile(raw: unknown): FieldProfile | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const userId = str(r.user_id);
  if (!userId) return null;
  const office = r.office && typeof r.office === 'object' ? (r.office as Record<string, unknown>) : null;
  const officeId = office ? num(office.id) : null;
  return {
    userId,
    name: str(r.name) ?? str(r.email) ?? 'Unnamed',
    email: str(r.email),
    displayName: str(r.display_name),
    fieldStartDate: str(r.field_start_date),
    fieldStartDateSet: r.field_start_date_set === true,
    fieldRole: ROLES.includes(r.field_role as FieldRole) ? (r.field_role as FieldRole) : null,
    fieldRoleSet: r.field_role_set === true,
    active: r.active !== false,
    activeFrom: str(r.active_from),
    activeUntil: str(r.active_until),
    workerType: WORKERS.includes(r.worker_type as WorkerType) ? (r.worker_type as WorkerType) : null,
    quietStart: str(r.quiet_start) ?? '21:00',
    quietEnd: str(r.quiet_end) ?? '08:00',
    workingDays: Array.isArray(r.working_days) ? r.working_days.filter((d): d is number => typeof d === 'number') : null,
    hasHomeAddress: r.has_home_address === true,
    office: office && officeId !== null ? { id: officeId, name: str(office.name) ?? '', timezone: str(office.timezone) } : null,
    teams: (Array.isArray(r.teams) ? r.teams : []).flatMap((t) => {
      const x = t as Record<string, unknown>;
      const id = num(x.id);
      return id === null ? [] : [{ id, name: str(x.name) ?? '', officeId: num(x.office_id) }];
    }),
    teamHistory: (Array.isArray(r.team_history) ? r.team_history : []).flatMap((h) => {
      const x = h as Record<string, unknown>;
      const joinedAt = str(x.joined_at);
      return joinedAt ? [{ teamId: num(x.team_id), teamName: str(x.team_name) ?? '', joinedAt, leftAt: str(x.left_at) }] : [];
    }),
    deactivation: parseDeactivation(r.deactivation),
  };
}

export function parseFieldProfiles(raw: unknown): FieldProfile[] {
  return (Array.isArray(raw) ? raw : []).map(parseFieldProfile).filter((p): p is FieldProfile => p !== null);
}

// -----------------------------------------------------------------------------
// The admin's edit: only the fields an admin may set (the rep's own are read-only here).
// -----------------------------------------------------------------------------

/**
 * ⚠️ `active` IS NOT HERE, and must not be added back (KNK-1.54 / FR-46, migration 20261010a1
 * DECISION 1). `save_field_rep_profile` refuses it: deactivating a rep is gated on `lead.manage`
 * at Everyone scope, previews what moves, moves their open Revisits and Claims to a chosen rep
 * and opens a 72-hour Knock quarantine. A switch on this dialog could do none of that, and
 * leaving it would have been a second door into the same room that skipped all of it.
 * `activeFrom` and `activeUntil` STAY: they are planning dates for a seasonal rep (FR-45) and
 * setting them moves nothing.
 */
export interface AdminDraft {
  fieldStartDate: string; // '' = derived from the first Knock
  fieldRole: FieldRole | 'derived';
  activeFrom: string;
  activeUntil: string;
  workerType: WorkerType | 'unset';
  displayName: string;
}

export function adminDraftFrom(p: FieldProfile): AdminDraft {
  return {
    fieldStartDate: p.fieldStartDateSet ? p.fieldStartDate ?? '' : '',
    fieldRole: p.fieldRoleSet && p.fieldRole ? p.fieldRole : 'derived',
    activeFrom: p.activeFrom ?? '',
    activeUntil: p.activeUntil ?? '',
    workerType: p.workerType ?? 'unset',
    displayName: p.displayName ?? '',
  };
}

/** The keys that changed, in the server's names. Empty and "derived" are sent as null. */
export function adminPayload(draft: AdminDraft, original: FieldProfile): Record<string, string | boolean | null> {
  const before = adminDraftFrom(original);
  const wire: Record<keyof AdminDraft, [string, string | boolean | null]> = {
    fieldStartDate: ['field_start_date', draft.fieldStartDate || null],
    fieldRole: ['field_role', draft.fieldRole === 'derived' ? null : draft.fieldRole],
    activeFrom: ['active_from', draft.activeFrom || null],
    activeUntil: ['active_until', draft.activeUntil || null],
    workerType: ['worker_type', draft.workerType === 'unset' ? null : draft.workerType],
    displayName: ['display_name', draft.displayName.trim() || null],
  };
  const out: Record<string, string | boolean | null> = {};
  for (const k of Object.keys(wire) as (keyof AdminDraft)[]) {
    const changed = k === 'displayName' ? draft.displayName.trim() !== before.displayName.trim() : draft[k] !== before[k];
    if (changed) out[wire[k][0]] = wire[k][1];
  }
  return out;
}

export function validateAdminDraft(d: AdminDraft): string | null {
  if (d.activeFrom && d.activeUntil && d.activeUntil < d.activeFrom) return FIELD_PROFILES_COPY.untilBeforeFrom;
  if (d.displayName.trim().length > 60) return FIELD_PROFILES_COPY.displayNameTooLong;
  return null;
}

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function workingDaysLine(days: readonly number[] | null): string {
  if (!days || days.length === 0) return 'Not declared';
  return [...days].sort((a, b) => a - b).map((d) => DAY_NAMES[d - 1] ?? '?').join(', ');
}

export function quietHoursLine(p: Pick<FieldProfile, 'quietStart' | 'quietEnd' | 'office'>): string {
  return `${p.quietStart} to ${p.quietEnd}${p.office?.timezone ? ` (${p.office.timezone})` : ' local'}`;
}

export function roleLabel(p: Pick<FieldProfile, 'fieldRole' | 'fieldRoleSet'>): string {
  if (!p.fieldRole) return 'None yet';
  const name = { setter: 'Setter', closer: 'Closer', both: 'Setter and closer' }[p.fieldRole];
  return p.fieldRoleSet ? name : `${name} (from their work)`;
}

// -----------------------------------------------------------------------------
// Offices
// -----------------------------------------------------------------------------

export interface OfficeTeam {
  id: number;
  name: string;
  isOffice: boolean;
  timezone: string | null;
  officeId: number | null;
}

export function parseOfficeTeams(rows: unknown): OfficeTeam[] {
  return (Array.isArray(rows) ? rows : []).flatMap((row) => {
    const r = row as Record<string, unknown>;
    const id = num(r.id);
    return id === null
      ? []
      : [{ id, name: str(r.name) ?? '', isOffice: r.is_office === true, timezone: str(r.timezone), officeId: num(r.office_id) }];
  });
}

export interface OfficeTree {
  offices: { office: OfficeTeam; teams: OfficeTeam[] }[];
  unplaced: OfficeTeam[];
}

const byName = (a: OfficeTeam, b: OfficeTeam) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) || a.id - b.id;

export function officeTree(teams: readonly OfficeTeam[]): OfficeTree {
  const offices = teams.filter((t) => t.isOffice).sort(byName);
  const ids = new Set(offices.map((o) => o.id));
  return {
    offices: offices.map((office) => ({ office, teams: teams.filter((t) => t.officeId === office.id).sort(byName) })),
    // A team pointing at something that is no longer an Office reads as unplaced, as the server treats it.
    unplaced: teams.filter((t) => !t.isOffice && (t.officeId === null || !ids.has(t.officeId))).sort(byName),
  };
}

/** The zones offered. The server accepts any IANA name; these are the ones a US field team needs. */
export const OFFICE_TIMEZONES: readonly string[] = [
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
];

export const FIELD_PROFILES_COPY = {
  officesTitle: 'Offices',
  officesDescription:
    'An Office is a team other teams sit under, with its own timezone. Reps join their own team, never the Office. ' +
    'The Office\'s manager reaches every team under it; a team lead reaches their own team, never a sibling team.',
  markOffice: 'Make an Office',
  unmarkOffice: 'Stop being an Office',
  noOffices: 'No Offices yet. Make one from an empty team, then place teams under it.',
  placeUnder: 'Office',
  noOffice: 'No Office',
  unplaced: 'Teams not under an Office',
  emptyTeamsOnly: 'Only a team with no members can become an Office: move its reps onto a team that will sit under it first.',
  profilesTitle: 'Field profiles',
  profilesDescription:
    'Each person\'s field details in this account. Quiet hours, working days and home address are the rep\'s own, set on their phone.',
  edit: 'Edit',
  save: 'Save',
  untilBeforeFrom: 'The active-until date cannot be before the active-from date.',
  displayNameTooLong: 'A display name is at most 60 characters.',
  derivedStart: 'Blank: the day of their first Knock.',
} as const;
